import { Get, Post } from "api";
import { useToast } from "@chakra-ui/react";
import moment from "moment";

interface SubmitUpdateParams {
  partnerId: number;
  updateCategories: string[];
  updateNote: string;
  date?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  toast: ReturnType<typeof useToast>;
}

const companyNameCollator = new Intl.Collator(undefined, {
  sensitivity: "base",
});

const sortByCompanyName = <T extends { companyName: string | null }>(
  partners: T[]
) =>
  partners.sort((a, b) =>
    companyNameCollator.compare(a.companyName || "", b.companyName || "")
  );

export const getPartners = async (date: string) => {
  try {
    const response = await Get(
      `/manage/partner/account-manager-updates?date=${date}`
    );

    if (response && Array.isArray(response)) {
      return sortByCompanyName(response);
    }

    return response;
  } catch (error) {
    return [];
  }
};

export const getAdminPartners = async (
  date: string,
  accountManagerId?: number | null
) => {
  try {
    const queryParams = new URLSearchParams();
    if (date) {
      queryParams.append("date", date);
    }
    if (accountManagerId) {
      queryParams.append("accountManagerId", String(accountManagerId));
    }

    const response = await Get(
      `/manage/partner/admin/account-manager-updates?${queryParams.toString()}`
    );

    if (response && Array.isArray(response)) {
      return sortByCompanyName(response);
    }
  } catch (error) {
    return [];
  }
};

export const submitAMUpdate = async ({
  partnerId,
  updateCategories,
  updateNote,
  date,
  onSuccess,
  onError,
  toast,
}: SubmitUpdateParams): Promise<boolean> => {
  try {
    if (updateCategories.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one category",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    const trimmedNote = updateNote.trim();
    if (!trimmedNote) {
      toast({
        title: "Error",
        description: "Please enter an update note",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    const wordCount = trimmedNote
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    if (wordCount < 3) {
      toast({
        title: "Error",
        description: "Note must contain at least 3 words",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (trimmedNote.length > 400) {
      toast({
        title: "Error",
        description: "Note must not exceed 400 characters",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    const response = await Post(`/manage/account-managers/updates`, {
      partnerId,
      dateFrom: date || moment(new Date()).format("YYYY-MM-DD"),
      updateCategories,
      updateNote: trimmedNote,
    });

    if (response.err) {
      throw new Error(response.error || "Failed to submit update");
    }

    toast({
      title: "Success",
      description: "Update added successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
    });

    if (onSuccess) {
      onSuccess();
    }

    return true;
  } catch (error) {
    console.error("Error submitting update:", error);
    toast({
      title: "Error",
      description: "Failed to submit update",
      status: "error",
      duration: 5000,
      isClosable: true,
    });

    if (onError) {
      onError(error);
    }

    return false;
  }
};

export const fetchAMUpdates = async (
  partnerId: number,
  page: number,
  limit: number,
  date?: string
) => {
  try {
    const response = await Post(
      `/manage/partners/${partnerId}/account-manager-updates`,
      {
        page,
        limit,
        ...(date ? { date } : {}),
      }
    );

    if (response.err) {
      throw new Error(response.error || "Failed to fetch updates");
    }

    return {
      items: response.items || [],
      count: response.count || 0,
    };
  } catch (error) {
    console.error("Error fetching updates:", error);
    throw error;
  }
};
